/*
Current flow:
I think a refactoring to make it easier would be welcome

╔══════════════════════════════════════════╗
║ useImportPhotosDialog                    ║
╟──────────────────────────────────────────╢
║ onResolve():                             ║
║ - log GA event                           ║
║ - convert to Photo objects               ║
║ - remove photos already used in editor   ║
║ - local: preprocess                      ║
║                                          ║
║ ╔══════════════════════════════════════╗ ║
║ ║ PhotoSourceDialog                    ║ ║
║ ╟──────────────────────────────────────╢ ║
║ ║ onChange():                          ║ ║
║ ║ - local: Check max #upload files     ║ ║
║ ║   pass as File                       ║ ║
║ ║ - smartphoto: Show selection popup   ║ ║
║ ║   pass as IPhotoSourceItem           ║ ║
║ ║ - partner: Show selection popup      ║ ║
║ ║   pass as IPhotoSourceItem           ║ ║
║ ║ - picker: Show external picker popup ║ ║
║ ║   pass as IPhotoSourceItem           ║ ║
║ ║                                      ║ ║
║ ║ ╔══════════════════════════════════╗ ║ ║
║ ║ ║ PhotoSources                     ║ ║ ║
║ ║ ╟──────────────────────────────────╢ ║ ║
║ ║ ║ onSelect(): login/connect        ║ ║ ║
║ ║ ║                                  ║ ║ ║
║ ║ ║ ╔══════════════════════════════╗ ║ ║ ║
║ ║ ║ ║ Local PhotoSource            ║ ║ ║ ║
║ ║ ║ ╟──────────────────────────────╢ ║ ║ ║
║ ║ ║ ║ onSelect(photoStore, files)  ║ ║ ║ ║
║ ║ ║ ╚══════════════════════════════╝ ║ ║ ║
║ ║ ║                                  ║ ║ ║
║ ║ ║ ╔══════════════════════════════╗ ║ ║ ║
║ ║ ║ ║ Other PhotoSources           ║ ║ ║ ║
║ ║ ║ ╟──────────────────────────────╢ ║ ║ ║
║ ║ ║ ║ onSelect(photoStore)         ║ ║ ║ ║
║ ║ ║ ╚══════════════════════════════╝ ║ ║ ║
║ ║ ╚══════════════════════════════════╝ ║ ║
║ ╚══════════════════════════════════════╝ ║
╚══════════════════════════════════════════╝
*/

import React, { ComponentPropsWithoutRef, useEffect, useRef } from "react";

import { useAppContext } from "../../../hooks/useAppContext";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useLabels } from "../../../hooks/useLabels";
import { SimpleDialog } from "../../dialog";
import { PhotoSources } from "../../photo-sources";
import { showImportPhotosDialogAsync } from "../import-photos-dialog";
import { showUploadLimitDialogAsync } from "../upload-limit-dialog";
import { UPLOAD_LIMIT } from "./constants";
import { labels } from "./labels";
import { PhotoSourceDialog } from "./photo-source-dialog";
import { ILocalPhotosImport } from "../../photo-sources/photo-source-local";
import { useSnackbar } from "../../../hooks/useSnackbar";

/** The photo store used + selection information from the store */
interface ISmartphotoPhotosImport {
	photoStore: eXpress.core.IPhotoStore;
	smartphotoPhotos: eXpress.client.IPhotoSourceItem[];
}

interface IPartnerPhotosImport {
	photoStore: eXpress.core.IPhotoStore;
	partnerPhotos: eXpress.client.IPhotoSourceItem[];
}

interface IPickerPhotosImport {
	photoStore: eXpress.core.IPhotoStore;
	pickerPhotos: eXpress.client.IPhotoSourceItem[];
}

export type PhotoStoreSelection =
	| ILocalPhotosImport
	| ISmartphotoPhotosImport
	| IPartnerPhotosImport
	| IPickerPhotosImport;

/**
 * A function that can be used to load some required data for this dialog up front
 * Remark: cached in SmartClient cache, not Tanstack query cache
 */
export function preloadAsync(
	client: Pick<eXpress.core.IClient, "getPhotoStoresAsync">,
	isAuthenticated: boolean,
) {
	return Promise.all([isAuthenticated ? client.getPhotoStoresAsync() : undefined]).then(
		() => undefined,
	);
}

/**
 * Show a dialog with PhotosSource.
 * When a store is clicked the files can be selected and are returned
 */
export const PhotoSourceDialogContainer = ({
	onClose,
	isOpen,
	...dialogProps
}: {
	onClose: (result: PhotoStoreSelection | undefined) => void;
} & ComponentPropsWithoutRef<typeof SimpleDialog>) => {
	const { editor, client, logger, platform } = useAppContext();
	const { createErrorHandler } = useErrorHandling();

	const [
		[titleLabel, numberOfEmptyPhotoZones, photoPickerFailedLabel, externalPickerConnectedLabel],
		areLabelsLoading,
	] = useLabels([
		labels.title,
		labels.numberOfEmptyPhotoZones,
		labels.photoPickerFailed,
		labels.externalPickerConnected,
	]);

	const photoPickerRef = useRef<Record<string, eXpress.client.IExternalPhotosPicker>>({});

	// If component is unmounted, cleanup possible picker session
	useEffect(() => {
		return () => {
			Object.values(photoPickerRef.current).forEach((picker) => {
				picker.closePickerAsync().catch(createErrorHandler());
			});
			photoPickerRef.current = {};
		};
	}, [createErrorHandler]);

	const { addSnackbar } = useSnackbar();

	if (areLabelsLoading) {
		return null;
	}

	let numberOfEmptyPhotoZonesLabel = "";
	if (editor.definition?.product.editorType !== eXpress.core.EditorType.PhotoBook) {
		const numberOfEmptyImageZonesInDef = editor.definition?.getEmptyPhotoZones(false).length;
		if (numberOfEmptyImageZonesInDef) {
			numberOfEmptyPhotoZonesLabel = eXpress.utils.formatString(
				numberOfEmptyPhotoZones,
				numberOfEmptyImageZonesInDef.toString(),
			);
		}
	}

	/**
	 * Only the selected photoStore with optionally the selected photos
	 * Photo selection iframe is opened here
	 */
	const onChange = (
		result:
			| {
					photoStore: eXpress.core.IPhotoStore | undefined;
			  }
			| ILocalPhotosImport
			| undefined,
		didLogin: boolean,
	) => {
		if (!result) {
			onClose(undefined);
		} else {
			const { photoStore } = result;
			if (!photoStore) return;

			const { storeKey } = photoStore;

			// local store returns a selection of files
			if ("files" in result) {
				const selectedPhotos = result.files;
				if (selectedPhotos.length <= UPLOAD_LIMIT) {
					onClose({
						photoStore,
						files: selectedPhotos,
					} satisfies ILocalPhotosImport);
				} else {
					showUploadLimitDialogAsync(UPLOAD_LIMIT).catch(createErrorHandler());
				}
			} else if (
				!photoStore.useExternalPhotoPicker &&
				photoStore.isConnected &&
				photoStore.hasAlbumAccess
			) {
				//  For any other store we still need to prompt for a selection
				// TODO: add option to specify a maximum number of photos so import limitation can be done inside the iframe
				showImportPhotosDialogAsync({
					storeKey,
				})
					.then((importResult) => {
						if (!importResult) {
							onClose(undefined);
						} else {
							if (photoStore.storeType === eXpress.core.StoreType.eXpress) {
								const smartphotoPhotos = (
									importResult as
										| eXpress.IImportedPhotoWithDateAsString[]
										| eXpress.IImportedPhoto[]
								).map((castedPhoto) => {
									return eXpress.convertSmartphotoToPhotoSource(castedPhoto);
								});

								onClose({
									photoStore,
									smartphotoPhotos,
								});
							} else {
								const partnerPhotos = (
									importResult as eXpress.core.IPartnerPhoto[]
								).map((selectedPhoto) =>
									eXpress.convertPartnerPhotoToPhotoSource(
										selectedPhoto,
										!photoStore.doesNotSupportCors,
										platform,
									),
								);

								onClose({
									photoStore,
									partnerPhotos,
								});
							}
						}
					})
					.catch((err: unknown) => {
						createErrorHandler()(err);
						addSnackbar({
							id: `photo-picker-${photoStore.storeType}`,
							text: photoPickerFailedLabel,
						});
					});
			} else if (
				photoStore.useExternalPhotoPicker &&
				photoStore.isConnected &&
				photoStore.hasAlbumAccess
			) {
				// A popup was opened to connect to the partner
				// Opening a second popup on other domain seem to be blocked by browser
				// So we stop an request the user to click the button again
				if (didLogin) {
					// UI Feedback for a second click
					addSnackbar({
						id: "photo-connected",
						text: eXpress.utils.formatString(
							externalPickerConnectedLabel,
							photoStore.name,
						),
					});
					return;
				}

				// Reuse the picker session if already available
				const pickerPromise = !photoPickerRef.current[photoStore.storeType]
					? eXpress.client.createExternalPhotosPicker(photoStore.storeType, {
							client,
							logger,
					  })
					: Promise.resolve(photoPickerRef.current[photoStore.storeType]);

				// Wait for selection in the picker
				pickerPromise
					.then(async (newPicker) => {
						// Remember picker while popup is open
						photoPickerRef.current[photoStore.storeType] = newPicker;

						await newPicker.openPickerAsync();
						return newPicker.waitForPhotosAsync();
					})
					.then((selection) => {
						if (selection) {
							onClose({
								pickerPhotos: selection.photos,
								photoStore,
							});
						}
					})
					.catch((err: unknown) => {
						createErrorHandler()(err);
						addSnackbar({
							id: `photo-picker-${photoStore.storeType}`,
							text: "Failed to selected photo", // TODO: CMS Label
						});
					});
			} else {
				// somehow we have no store (example user didn't finish store authentication successfully)
				onClose(undefined);
			}
		}
	};

	return (
		<PhotoSourceDialog
			open={isOpen}
			{...dialogProps}
			onClose={() => {
				onChange(undefined, false);
			}}
			title={titleLabel}
			numberOfEmptyPhotoZonesLabel={numberOfEmptyPhotoZonesLabel}
		>
			<PhotoSources onSelect={onChange} />
		</PhotoSourceDialog>
	);
};
PhotoSourceDialogContainer.displayName = "PhotoSourceDialogContainer";
